import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ResourceOption } from '@kouto/types';
import { RadixSelect, SelectOption } from 'components/common/RadixSelect';

type ResourceAttribute = ResourceOption['attributes'][number];

export interface ResourceAttributePick {
  option: ResourceOption;
  participantIndex: number;
  resourceAttributeId: string;
  resourceAttributeValueId: string;
}

export interface ResourceAttributeSelectProps {
  attribute: ResourceAttribute;
  compatibleValueIds: Set<string>;
  earlierPickLabels: string[];
  onSelect: (pick: ResourceAttributePick) => void;
  option: ResourceOption;
  participantIndex: number;
  portalContainer?: HTMLElement | null;
  selectableValueIds: Set<string>;
  selectedValueId?: string;
}

const ResourceAttributeSelect = ({
  attribute,
  compatibleValueIds,
  earlierPickLabels,
  onSelect,
  option,
  participantIndex,
  portalContainer,
  selectableValueIds,
  selectedValueId,
}: ResourceAttributeSelectProps) => {
  const { t: translate } = useTranslation();

  const selectOptions: SelectOption[] = [...attribute.values]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((value) => {
      const isSelectable =
        selectableValueIds.has(value.resourceAttributeValueId) ||
        value.resourceAttributeValueId === selectedValueId;
      const isRuledOutByPicks =
        earlierPickLabels.length > 0 &&
        !compatibleValueIds.has(value.resourceAttributeValueId);
      const unavailableLabel = isRuledOutByPicks
        ? translate('resourceValueUnavailableWith', {
            label: value.label,
            picks: earlierPickLabels.join(', '),
          })
        : translate('resourceValueSoldOut', { label: value.label });
      return {
        disabled: !isSelectable,
        label: isSelectable ? value.label : unavailableLabel,
        value: value.resourceAttributeValueId,
      };
    });

  const handleValueChange = (resourceAttributeValueId: string) => {
    onSelect({
      option,
      participantIndex,
      resourceAttributeId: attribute.resourceAttributeId,
      resourceAttributeValueId,
    });
  };

  return (
    <FieldLabel>
      {attribute.name}
      <RadixSelect
        aria-label={`${option.resourcePoolName} ${attribute.name}`}
        className="resource-attribute-select"
        onValueChange={handleValueChange}
        options={selectOptions}
        placeholder={translate('selectResourceAttribute', {
          attributeName: attribute.name,
        })}
        portalContainer={portalContainer}
        value={selectedValueId ?? ''}
      />
    </FieldLabel>
  );
};

export default ResourceAttributeSelect;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  color: var(--way-colors-primaryTextColor);
`;
